import { FC, useCallback, useContext } from 'react'
import NextImage from '../../common/next-image'
import { ScreenContext } from 'react-components'
import { BannerSlot } from '../../../transports/home-page-transport'
import {
    Section,
    BannerContainer,
    BannerPicture,
    BannerContent,
    BannerHeading,
    BannerDescription,
    BannerButton,
} from './styles'
import AnalyticsService from '../../../services/analytics-service'
import { landingPageEventCategories } from '../../../services/analytics-service/categories'
import { replaceArrivalDateWithToday } from '../../../utils/date-utils'

type Props = {
    banner: BannerSlot | null
    // Sizes the container to the image's own aspect ratio instead of a fixed box (no crop/stretch, no letterboxing).
    fitToImage?: boolean
    imageFormat?: 'medium' | 'large'
}

const BannerSection: FC<Props> = ({ banner, fitToImage, imageFormat = 'large' }) => {
    const { isMobile, isTablet } = useContext(ScreenContext)
    const handleBannerClick = useCallback(() => {
        AnalyticsService.trackClickEvent(
            landingPageEventCategories.bannerAdSection.BANNER_CLICK,
            banner?.bannerKey ?? '',
        )
        if (banner?.url) {
            window.open(replaceArrivalDateWithToday(banner.url), '_blank', 'noopener,noreferrer')
        }
    }, [banner?.url, banner?.bannerKey])

    const deviceImage =
        (isMobile && banner?.image?.mobile) || (isTablet && banner?.image?.tablet) || banner?.image?.desktop

    const selectedFormat = deviceImage?.formats?.[imageFormat]
    const preferOriginal = fitToImage && (deviceImage?.width ?? 0) > (selectedFormat?.width ?? 0)
    const resolvedImage = preferOriginal ? deviceImage : (selectedFormat ?? deviceImage)
    const imageUrl = resolvedImage?.url
    const canFitToImage = !!(fitToImage && resolvedImage?.width && resolvedImage?.height)

    return (
        <Section>
            {banner && imageUrl && (
                <BannerContainer onClick={handleBannerClick} $fitToImage={canFitToImage}>
                    <BannerPicture $fitToImage={canFitToImage}>
                        {canFitToImage ? (
                            <NextImage
                                src={imageUrl}
                                alt={banner.heading || 'Banner'}
                                width={resolvedImage.width}
                                height={resolvedImage.height}
                                sizes="(max-width: 1024px) 100vw"
                            />
                        ) : (
                            <NextImage
                                src={imageUrl}
                                alt={banner.heading || 'Banner'}
                                objectFit="fill"
                                sizes="(max-width: 1024px) 100vw, 79.75rem"
                            />
                        )}
                    </BannerPicture>
                    <BannerContent>
                        <BannerHeading $color={banner.color}>{banner.heading}</BannerHeading>
                        {!!banner.description && (
                            <BannerDescription $color={banner.color}>{banner.description}</BannerDescription>
                        )}
                        {!!banner.buttonText && !!banner.url && (
                            <BannerButton onClick={handleBannerClick}>{banner.buttonText}</BannerButton>
                        )}
                    </BannerContent>
                </BannerContainer>
            )}
        </Section>
    )
}

export default BannerSection
